/**
 * Bottom-sheet gesture layer (Mobile P12).
 *
 * P11 made every kit dialog LOOK like a sheet on a phone with one CSS block.
 * This file makes it BEHAVE like one — drag to dismiss, with a velocity-projected
 * snap on release. The two are deliberately separable: delete this and the CSS
 * sheet still works, it just cannot be flung away.
 *
 * D2 holds throughout: the sheet card IS `.pb-modal`. There is no second DOM
 * shape, so every per-modal CSS hook and every `createModal` return key keeps
 * working, and a converged call site needs zero edits.
 *
 * ── The four-handler discipline ──────────────────────────────────────────
 * Copied from `editing/iframe-bridge-layers/reorder-preview.js`, which is the
 * only drag in this repo that gets it right. `pointerdown` / `pointermove` /
 * `pointerup` is the part everyone writes; `pointercancel`, `lostpointercapture`
 * and `window blur` are the ones that get omitted, and their absence is exactly
 * what leaves a sheet stranded mid-drag with no way back — the OS took the
 * pointer (a system gesture, a phone call, the tab backgrounding) and the drag
 * never heard about it.
 *
 * `finish(reason)` is a single idempotent funnel that every exit path runs
 * through, so "restore the transform" cannot be forgotten on one branch.
 *
 * ── What this file does NOT do ───────────────────────────────────────────
 * No body scroll-lock. `html, body { overflow: hidden }` plus
 * `.pb-app { height: 100dvh; overflow: hidden }` is already a permanent static
 * lock, so there is no document scroll position to save or restore, and code
 * that "restores" one would be inventing a value.
 */

import { prefersReducedMotion } from './feedback.js';
import { projectSheetRelease, resolveSheetSnap } from '../shared/touch-geometry.js';
import { queueModalStackSync } from './modal-primitives.js';

/** Axis lock: past this much travel, the gesture commits to one direction. */
const AXIS_LOCK_PX = 8;

/**
 * A finger parked longer than this has no velocity, whatever its last move was.
 * Without it, "drag 15px, hold for a second, lift" projects as though the last
 * 15px happened in the instant before release.
 */
const VELOCITY_STALE_MS = 66;

/**
 * How far a MODAL sheet must be thrown before a release dismisses it.
 *
 * Proportional to the card, not a fixed number. The plan's `lowestDetent - 24`
 * was written for P13's detented inspectors, where the detents are real heights
 * and 24px is a nudge past the last one. A modal sheet has ONE detent — open —
 * so the same 24px means a 25px drag throws the dialog away, which is nowhere
 * near enough travel to be a decision. The floor matters more than the ratio in
 * jsdom and on short dialogs, where measurement returns 0.
 */
const DISMISS_MIN_PX = 96;
const DISMISS_HEIGHT_RATIO = 0.28;

/** Interactive descendants a header drag must not steal from. */
const HEADER_INTERACTIVE = 'button,a,input,select,textarea,[contenteditable="true"]';

/**
 * Decide whether a pointerdown may start a sheet drag, and say why.
 *
 * Exported for its own sake: the origin rules are the part most likely to be
 * quietly broken by a later edit, and they are much easier to assert directly
 * than through a simulated gesture.
 *
 * Priority order:
 *   1. the grabber — always; it exists for exactly this
 *   2. the header — unless the press landed on something interactive
 *   3. the body — ONLY when already scrolled to the top. Without that rule the
 *      sheet and the body scroller fight over every downward swipe, and the
 *      sheet wins, which makes the content unreadable.
 *
 * @returns {{allowed: boolean, origin: 'grabber'|'header'|'body'|null, requiresDownward: boolean}}
 */
export function resolveDragOrigin(target, { grabber, header, body } = {}) {
  const el = target instanceof Element ? target : null;
  if (!el) return { allowed: false, origin: null, requiresDownward: false };

  if (grabber && (el === grabber || grabber.contains(el))) {
    return { allowed: true, origin: 'grabber', requiresDownward: false };
  }
  if (header && (el === header || header.contains(el))) {
    if (el.closest(HEADER_INTERACTIVE)) {
      return { allowed: false, origin: null, requiresDownward: false };
    }
    return { allowed: true, origin: 'header', requiresDownward: false };
  }
  if (body && (el === body || body.contains(el))) {
    // The scroll-handoff rule. `scrollTop <= 0` is checked at pointerdown and
    // the downward requirement at the first move, because a body that is
    // scrolled to the top may still legitimately be scrolled UP-ward into
    // overscroll — that is the scroller's gesture, not the sheet's.
    if ((body.scrollTop || 0) > 0) return { allowed: false, origin: null, requiresDownward: false };
    return { allowed: true, origin: 'body', requiresDownward: true };
  }
  return { allowed: false, origin: null, requiresDownward: false };
}

/**
 * Attach drag-to-dismiss to a sheet card.
 *
 * @param {object} opts
 * @param {HTMLElement} opts.card    — the `.pb-modal.pb-sheet` element
 * @param {HTMLElement} [opts.grabber]
 * @param {HTMLElement} [opts.header]
 * @param {HTMLElement} [opts.body]
 * @param {number[]} [opts.detents=[0]] — downward offsets from open, ascending
 * @param {number} [opts.slackPx] — travel past the last detent before a release
 *   dismisses. Defaults to a proportion of the card height; P13 passes its own.
 * @param {() => void} opts.onDismiss   — called on a release past the last detent
 * @param {(detent: number, index: number) => void} [opts.onSnap]
 * @param {() => number} [opts.onDragStart] — called once the axis locks to Y.
 *   Returns the offset the drag should START from, so a sheet resting at a
 *   detent tracks the finger from there rather than jumping to fully open.
 * @param {() => number} [opts.now]     — injectable clock, for tests
 * @returns {{destroy: () => void}}
 */
export function attachSheetDrag(opts = {}) {
  const {
    card,
    grabber = null,
    header = null,
    body = null,
    detents = [0],
    slackPx = null,
    onDismiss,
    onSnap,
    onDragStart,
    now = () => Date.now()
  } = opts;

  if (!card) return { destroy() {} };

  /** @type {null | object} */
  let drag = null;
  let destroyed = false;

  /**
   * Measured per release, not cached: a sheet's height changes when its content
   * loads, when the keyboard opens, and when the device rotates.
   */
  const dismissSlackPx = () => {
    if (slackPx != null) return slackPx;
    const height = card.getBoundingClientRect?.().height || 0;
    return Math.max(DISMISS_MIN_PX, height * DISMISS_HEIGHT_RATIO);
  };

  const setOffset = (px) => {
    card.style.transform = px > 0 ? `translateY(${px}px)` : '';
  };

  /**
   * The single idempotent exit. EVERY path ends here — release, cancel, lost
   * capture, blur, destroy — so there is exactly one place that restores the
   * card, and it cannot be skipped on a branch someone forgot to write.
   */
  const finish = (reason) => {
    if (!drag) return;
    const state = drag;
    drag = null;

    try {
      if (state.captured && card.hasPointerCapture?.(state.pointerId)) {
        card.releasePointerCapture(state.pointerId);
      }
    } catch {
      // Expected flow: the capture is already gone precisely when the pointer
      // was taken away from us, which is the case this handler exists for.
    }
    card.classList.remove('pb-sheet-dragging');

    // Anything that is not a clean release snaps straight back to open. A
    // cancelled gesture must never dismiss: the user did not choose it.
    if (reason !== 'release') {
      card.style.transform = '';
      return;
    }

    // A finger held still before lifting has stopped, whatever it did before.
    const sinceMove = now() - state.lastMoveAt;
    const velocity = state.lastMoveAt && sinceMove <= VELOCITY_STALE_MS ? state.velocity : 0;
    const projected = projectSheetRelease(state.offset, velocity);
    const snap = resolveSheetSnap(projected, detents, dismissSlackPx());

    if (snap.dismiss) {
      if (typeof onDismiss === 'function') onDismiss();
      return;
    }
    if (!prefersReducedMotion()) card.classList.add('pb-sheet-settling');
    setOffset(snap.detent);
    if (typeof onSnap === 'function') onSnap(snap.detent, snap.index);
  };

  const onPointerDown = (e) => {
    if (drag || destroyed) return;
    if (e.button !== 0) return; // primary button / any touch contact only
    const { allowed, origin, requiresDownward } = resolveDragOrigin(e.target, {
      grabber,
      header,
      body
    });
    if (!allowed) return;

    drag = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origin,
      requiresDownward,
      axis: null,
      baseOffset: 0,
      offset: 0,
      lastY: e.clientY,
      velocity: 0,
      lastMoveAt: 0,
      captured: false
    };
  };

  const onPointerMove = (e) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;

    if (!drag.axis) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      // Axis lock, so a horizontal swipe inside the body — a carousel, a
      // scrollable row of chips — is not read as a failed sheet drag.
      drag.axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'x';
      if (drag.axis === 'x' || (drag.requiresDownward && dy <= 0)) {
        finish('rejected');
        return;
      }
      // A tap must retain the pressed button as its native click target.
      // Document listeners track the pending gesture until its axis is known.
      try {
        card.setPointerCapture(e.pointerId);
        drag.captured = true;
      } catch {
        // Expected flow: jsdom lacks capture and detached nodes refuse it.
        // Document-level move/up/cancel tracking remains active.
      }
      card.classList.add('pb-sheet-dragging');
      card.classList.remove('pb-sheet-settling');
      // The caller may re-base the drag — a non-modal sheet resting at `peek`
      // is already 400px down, and a finger moving 10px should read as 410,
      // not 10. Called AFTER the axis lock so a rejected gesture never
      // re-bases anything.
      const base = typeof onDragStart === 'function' ? onDragStart() : 0;
      drag.baseOffset = Number.isFinite(base) ? base : 0;
    }

    // Upward drag is resisted rather than followed: a sheet does not grow past
    // its open position, and letting it would expose whatever is behind it.
    const offset = Math.max(0, drag.baseOffset + dy);
    const at = now();
    // Velocity is measured ACROSS the move, not from the move to the release.
    // Dividing the last move's distance by the time that elapsed AFTER it is a
    // different quantity entirely, and it made a leisurely 15px drag look like
    // a flick.
    const dt = drag.lastMoveAt ? Math.max(1, at - drag.lastMoveAt) : 1;
    drag.velocity = (e.clientY - drag.lastY) / dt;
    drag.lastY = e.clientY;
    drag.lastMoveAt = at;
    drag.offset = offset;
    setOffset(offset);
    if (e.cancelable) e.preventDefault();
  };

  const onPointerUp = (e) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    // A press that never passed the axis lock is a tap, not a drag.
    finish(drag.axis === 'y' ? 'release' : 'rejected');
  };

  const onPointerCancel = (e) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    finish('pointercancel');
  };

  const onLostCapture = (e) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    finish('lostpointercapture');
  };

  const onBlur = () => finish('blur');

  card.addEventListener('pointerdown', onPointerDown);
  // Document-scoped, so a finger that leaves the card mid-drag is still tracked.
  document.addEventListener('pointermove', onPointerMove, { passive: false });
  document.addEventListener('pointerup', onPointerUp);
  document.addEventListener('pointercancel', onPointerCancel);
  document.addEventListener('lostpointercapture', onLostCapture);
  window.addEventListener('blur', onBlur);

  return {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      finish('destroy');
      card.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
      document.removeEventListener('lostpointercapture', onLostCapture);
      window.removeEventListener('blur', onBlur);
    }
  };
}

/**
 * Build the grabber — the visual affordance that says "this can be dragged".
 *
 * `aria-hidden`, because it is decoration: the sheet is already dismissible by
 * its close button, Escape and the backdrop, all of which are reachable without
 * a gesture. A screen-reader user has no use for a handle they cannot fling.
 */
export function createSheetGrabber() {
  const grabber = document.createElement('div');
  grabber.className = 'pb-sheet-grabber';
  grabber.dataset.pbKitPart = 'pb-sheet-grabber';
  grabber.setAttribute('aria-hidden', 'true');
  grabber.appendChild(document.createElement('span'));
  return grabber;
}

// ── Non-modal detented sheets (Mobile P13) ────────────────────────────────

/**
 * Detent heights, as a fraction of the usable viewport.
 *
 * `full` is deliberately NOT 1. The entire reason this sheet is non-modal is
 * that the canvas above it stays visible and hit-testable — a full-height
 * "non-modal" sheet is a modal that forgot its scrim, and the user loses the
 * thing they are editing while editing it.
 */
export const SHEET_DETENT_RATIOS = Object.freeze({ peek: 0.24, half: 0.5, full: 1 });
export const SHEET_PEEK_MIN_PX = 168;
/** Canvas left visible at the `full` detent. */
export const SHEET_FULL_GAP_PX = 56;

/**
 * Resolve the three detent HEIGHTS for a usable viewport height.
 *
 * Pure, and exported, because "how tall is `half`" is the kind of thing that
 * silently drifts and is much easier to assert directly than to measure.
 *
 * @param {number} usableHeightPx — visual viewport height MINUS the keyboard
 * @returns {{peek: number, half: number, full: number}}
 */
export function computeSheetDetents(usableHeightPx) {
  const usable = Number.isFinite(usableHeightPx) && usableHeightPx > 0 ? usableHeightPx : 0;
  const full = Math.max(0, usable - SHEET_FULL_GAP_PX);
  // A peek that has grown past `half` on a short viewport would make the ladder
  // non-monotonic, and resolveSheetSnap would then have two stops at the same
  // place. Clamped rather than assumed.
  const peek = Math.min(full, Math.max(SHEET_PEEK_MIN_PX, usable * SHEET_DETENT_RATIOS.peek));
  const half = Math.min(full, Math.max(peek, usable * SHEET_DETENT_RATIOS.half));
  return { peek, half, full };
}

/**
 * Convert detent HEIGHTS into the downward OFFSETS `resolveSheetSnap` expects.
 *
 * The card is always laid out at its `full` height and pushed down to show
 * less — the same mechanic as the modal sheet, so `attachSheetDrag` is reused
 * verbatim rather than forked. Offsets ascend as the sheet gets shorter.
 */
export function detentOffsets({ peek, half, full }) {
  return [0, Math.max(0, full - half), Math.max(0, full - peek)];
}

/** Detent name for an offset index, in the order `detentOffsets` produces. */
const OFFSET_INDEX_TO_NAME = ['full', 'half', 'peek'];

/**
 * Build a non-modal bottom sheet.
 *
 * NO overlay element, and that is the point (D1): there is no scrim to
 * intercept pointer events, so the canvas above stays live. It is therefore
 * NOT a `.pb-modal-overlay` member and gets no stack management — see the
 * `syncModalStack()` hook in modal-primitives.js, which inerts these when a
 * real modal opens.
 *
 * Reuses `createDrawerShell()` verbatim, so the drawer migration is additive
 * rather than a rewrite.
 *
 * @param {object} opts
 * @param {string} opts.id
 * @param {string} opts.title
 * @param {'peek'|'half'|'full'} [opts.detent='half']
 * @param {() => number} [opts.usableHeight] — defaults to the live viewport
 * @param {() => void} [opts.onClose]
 * @param {(name: string) => void} [opts.onDetent]
 * @param {(o: object) => object} opts.createShell — INJECTED, not imported.
 *   `components.js` imports this module, so importing `createDrawerShell` back
 *   would be a cycle. `components.js` exports `createSheetDrawer`, which is this
 *   function with the shell already bound; prefer that at call sites.
 * @returns {{sheet, header, titleEl, closeBtn, body, setDetent, close, destroy}}
 */
export function createNonModalSheet(opts = {}) {
  const { id, title, detent = 'half', usableHeight, onClose, onDetent, createShell, now } = opts;

  if (typeof createShell !== 'function') {
    throw new TypeError(
      'createNonModalSheet needs a createShell injector — import createSheetDrawer ' +
        'from ui/components.js instead of calling this directly'
    );
  }
  const shell = createShell({ id, title });
  const sheet = shell.drawer;
  sheet.classList.add('pb-sheet', 'pb-sheet--nonmodal');
  sheet.dataset.pbKit = 'sheet';
  sheet.setAttribute('aria-modal', 'false');

  const grabber = createSheetGrabber();
  sheet.insertBefore(grabber, sheet.firstChild);
  shell.header.classList.add('pb-sheet-header');
  shell.header.dataset.pbKitPart = 'pb-sheet-header';
  shell.body.classList.add('pb-sheet-body');
  shell.body.dataset.pbKitPart = 'pb-sheet-body';

  const measure = () =>
    typeof usableHeight === 'function' ? usableHeight() : window.innerHeight || 0;

  let current = detent;
  let heights = computeSheetDetents(measure());

  /**
   * At REST the sheet is sized by height, not pushed down by a transform.
   *
   * Found by a browser probe: translating a full-height card down to show
   * `half` puts its bottom edge — and its `env(safe-area-inset-bottom)`
   * padding, and the tail of its scrollable body — BELOW the viewport. The
   * content nearest the user's thumb then sits unprotected on the home
   * indicator, and scrolling the body moves content into a region that cannot
   * be seen. Height at rest keeps the bottom edge where the viewport ends.
   *
   * The transform is transient: `attachSheetDrag` writes it while the finger is
   * down, and `onDragStart` below expands the card to full height first so that
   * transform means the same thing at every detent.
   */
  const applyDetent = (name) => {
    current = name;
    heights = computeSheetDetents(measure());
    const height = heights[name] ?? heights.half;
    sheet.style.setProperty('--pb-sheet-h', `${Math.round(height)}px`);
    sheet.style.transform = '';
    sheet.dataset.pbDetent = name;
  };

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    drag.destroy();
    sheet.removeEventListener('keydown', onKeyDown);
    sheet.remove();
    queueModalStackSync();
    if (typeof onClose === 'function') onClose();
  };

  // Escape is wired DIRECTLY on the element, not through focus-trap: that
  // module dispatches `trap-escape` only to the top of the trap stack, and a
  // non-modal sheet is deliberately not on it — being on the stack is what
  // would make the canvas behind it unreachable.
  const onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    close();
  };
  sheet.addEventListener('keydown', onKeyDown);
  shell.closeBtn.addEventListener('click', close);

  applyDetent(current);
  document.body.appendChild(sheet);
  // Mounted, so ask for a stack sync: if a modal is ALREADY open, this sheet
  // must arrive inert rather than waiting for the next modal event. The
  // observer in modal-primitives.js only watches for overlays, by design.
  queueModalStackSync();

  const drag = attachSheetDrag({
    card: sheet,
    grabber,
    header: shell.header,
    body: shell.body,
    detents: detentOffsets(heights),
    // An explicit slack, unlike the modal sheet's height-proportional one: the
    // deepest detent here is `peek`, a real resting place, so the extra travel
    // that means "throw it away" is a nudge past it rather than a proportion
    // of a card the user has already shrunk.
    slackPx: 48,
    onDismiss: close,
    // Expand to the full-height coordinate space, and tell the drag where the
    // current detent sits in it, so the finger tracks 1:1 from any resting
    // position.
    onDragStart: () => {
      heights = computeSheetDetents(measure());
      sheet.style.setProperty('--pb-sheet-h', `${Math.round(heights.full)}px`);
      const index = OFFSET_INDEX_TO_NAME.indexOf(current);
      return detentOffsets(heights)[index >= 0 ? index : 1];
    },
    onSnap: (_offset, index) => {
      // Back to height-at-rest: applyDetent clears the transform the drag left.
      applyDetent(OFFSET_INDEX_TO_NAME[index] || 'half');
      if (typeof onDetent === 'function') onDetent(current);
    },
    now
  });

  return {
    sheet,
    header: shell.header,
    titleEl: shell.titleEl,
    closeBtn: shell.closeBtn,
    body: shell.body,
    getDetent: () => current,
    setDetent: applyDetent,
    close,
    destroy: close
  };
}
